import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect, useState } from 'react';
import { Database, DollarSign, Infinity, Shield } from 'lucide-react';

const AnimatedCounter = ({ end, decimals = 0, prefix = '', suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      let startTime;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        setCount(easeOut * end);
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [inView, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count.toFixed(decimals)}{suffix}
    </span>
  );
};

export const PriceBreakdown = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const stats = [
    {
      icon: Database,
      value: 500,
      suffix: '+',
      label: 'Curated backlink sources',
      color: '#3B82F6',
    },
    {
      icon: DollarSign,
      value: 0.018,
      prefix: '$',
      decimals: 3,
      label: 'Per unique source',
      color: '#22C55E',
    },
    {
      icon: Infinity,
      isInfinity: true,
      label: 'Unlimited Projects',
      color: '#8B5CF6',
    },
    {
      icon: Shield,
      isText: true,
      text: 'Lifetime',
      label: 'Access & updates',
      color: '#EC4899',
    },
  ];

  return (
    <section
      id="price-breakdown"
      data-testid="price-breakdown-section"
      className="section-dark py-16 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
            Why $9? Break it down:
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            With 500+ unique backlink sources in the directory, that's approximately{' '}
            <span className="text-green-400 font-semibold">$0.018 per unique backlink opportunity</span> — 
            less than 2 cents per curated, DA-filtered, spam-scored backlink source. And the directory keeps growing.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center p-6 rounded-2xl bg-white/5 border border-white/10"
              data-testid={`stat-card-${index}`}
            >
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                style={{ backgroundColor: `${stat.color}20` }}
              >
                <stat.icon size={24} style={{ color: stat.color }} />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                {stat.isInfinity ? (
                  <span className="gradient-text">∞</span>
                ) : stat.isText ? (
                  <span className="gradient-text">{stat.text}</span>
                ) : (
                  <AnimatedCounter
                    end={stat.value}
                    prefix={stat.prefix || ''}
                    suffix={stat.suffix || ''}
                    decimals={stat.decimals || 0}
                  />
                )}
              </div>
              <p className="text-sm text-white/60">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PriceBreakdown;
